<template>
    <div class="recommendations-container" v-if="recommendations.length > 0 || loading">
        <div class="section-header">
            <h2 class="section-title">
                <span class="section-icon">
                    <vue-feather type="star" size="16" stroke="white"></vue-feather>
                </span>
                {{ title }}
            </h2>
        </div>
        <div v-if="loading" class="loading-recommendations">
            <vue-feather type="loader" animation="spin" size="24"></vue-feather>
            <span>Loading recommendations...</span>
        </div>
        <div v-else-if="recommendations.length === 0" class="no-recommendations">
            <vue-feather type="video" size="24"></vue-feather>
            <span>No recommendations available</span>
        </div>
        <div v-else class="video-container col-4">
            <VideoThumb
                v-for="video in recommendations"
                :key="video.id"
                :video="video"
            />
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { getRecommendationsQuery, listVideosWithTags } from '../graphql/custom';
import VideoThumb from './VideoThumb.vue';

export default {
    name: 'VideoRecommendations',
    components: {
        VideoThumb
    },
    props: {
        currentUser: {
            type: Object,
            default: null
        },
        currentVideo: {
            type: Object,
            default: null,
            required: false
        },
        title: {
            type: String,
            default: 'Recommended Videos'
        }
    },
    data() {
        return {
            recommendations: [],
            loading: true
        }
    },
    async created() {
        await this.fetchRecommendations();
    },
    watch: {
        currentVideo: {
            handler() {
                this.fetchRecommendations();
            },
            deep: true
        }
    },
    methods: {
        async fetchRecommendations() {
            if (!this.currentVideo?.id) {
                this.loading = false;
                return;
            }

            try {
                this.loading = true;

                const res = await API.graphql({
                    query: getRecommendationsQuery,
                    variables: {
                        videoId: this.currentVideo.id,
                        limit: 12
                    }
                });

                const items = res.data.getRecommendations?.items || [];
                // Map flat owner fields to nested owner object for VideoThumb compatibility
                this.recommendations = items.map(v => ({
                    ...v,
                    owner: {
                        id: v.ownerId,
                        username: v.ownerUsername,
                        displayName: v.ownerDisplayName,
                        legacyAvatar: v.ownerLegacyAvatar,
                    }
                }));

            } catch (error) {
                console.error('Error fetching recommendations from Lambda, falling back:', error);
                await this.fetchRecommendationsFallback();
            } finally {
                this.loading = false;
            }
        },

        async fetchRecommendationsFallback() {
            try {
                const videosRes = await API.graphql({
                    query: listVideosWithTags,
                    variables: {
                        filter: {
                            id: { ne: this.currentVideo?.id || '' },
                            status: { eq: 'ready' },
                            publishState: { eq: 'published' }
                        },
                        limit: 12
                    }
                });

                this.recommendations = (videosRes.data.listVideoModels?.items || [])
                    .filter(video => this.isPublicVideo(video))
                    .sort((a, b) => {
                        const viewScore = (b.viewsCount || 0) - (a.viewsCount || 0);
                        const timeScore = new Date(b.createdAt) - new Date(a.createdAt);
                        return viewScore + timeScore;
                    });
            } catch (error) {
                console.error('Fallback recommendations also failed:', error);
                this.recommendations = [];
            }
        },
        isPublicVideo(video) {
            return video &&
                video.status === 'ready' &&
                (!video.publishState || video.publishState === 'published');
        }
    }
}
</script>

<style scoped>
.recommendations-container {
    background: var(--bg-secondary);
    padding: 20px 0;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px 12px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #3f51b5;
    flex-shrink: 0;
}

.loading-recommendations {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: var(--text-tertiary);
    gap: 12px;
}

.no-recommendations {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: var(--text-tertiary);
    gap: 12px;
}

.video-container {
    display: grid;
    gap: 20px;
    padding: 0 20px;
}

.video-container.col-3 {
    grid-template-columns: repeat(3, 1fr);
}

.video-container.col-4 {
    grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 1200px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 992px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .video-container.col-3,
    .video-container.col-4 {
        grid-template-columns: 1fr;
    }
}
</style>
